import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tabs, Modal } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import { getStockTransfersApi, createStockTransferApi, completeStockTransferApi, cancelStockTransferApi, getUniformItemsApi, getWarehouseStockApi } from 'api/services/uniformManagement';
import { useUniformMgmt } from '../../context/uniformMgmtContext';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { itemRender, handlePageChange, defaultPagination, pageSizeOptions } from 'utils/table';
import { formatDateHandler } from 'utils/common';
import { getStatusBadge } from '../../constants';
import Breadcrumbs from 'components/Breadcrumbs';
import EssFullWidthModal from 'components/modelpopup/baseModal';

const transferSchema = Yup.object().shape({
  source_warehouse: Yup.object().nullable().required('Source warehouse is required'),
  destination_warehouse: Yup.object().nullable().required('Destination warehouse is required'),
  uniform_item: Yup.object().nullable().required('Uniform item is required'),
  size: Yup.object().nullable().when('uniform_item', {
    is: (item) => !!item && (item.sizes?.length ?? 0) > 0,
    then: (s) => s.required('Size is required'),
    otherwise: (s) => s.notRequired(),
  }),
  quantity: Yup.number().required('Quantity is required').min(1, 'Must be at least 1'),
  transfer_date: Yup.string().required('Transfer date is required'),
  notes: Yup.string()
});

const TransferList = () => {
  const navigate = useNavigate();
  const { warehouseOptions, warehousesLoading } = useUniformMgmt();
  const [uniformItemsLoading, setUniformItemsLoading] = useState(false);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [pageSettings, setPageSettings] = useState({ ...defaultPagination });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [uniformItems, setUniformItems] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);

  const { control, handleSubmit, formState: { errors }, reset, setError, watch, setValue } = useForm({
    resolver: yupResolver(transferSchema),
    defaultValues: {
      source_warehouse: null, destination_warehouse: null,
      uniform_item: null, size: null, quantity: '', transfer_date: '', notes: ''
    }
  });

  const selectedItem = watch('uniform_item');
  const selectedSize = watch('size');
  const selectedSource = watch('source_warehouse');
  const sizeOptions = (selectedItem?.sizes || [])
    .filter(s => s.status !== false)
    .map(s => ({ label: `${s.name} (${s.code})`, value: s.id }));
  const sizeRequired = sizeOptions.length > 0;

  const [availableQty, setAvailableQty] = useState(null);
  const [availableLoading, setAvailableLoading] = useState(false);

  useEffect(() => {
    if (!selectedSource || !selectedItem) {
      setAvailableQty(null);
      return;
    }
    if (sizeRequired && !selectedSize) {
      setAvailableQty(null);
      return;
    }
    setAvailableLoading(true);
    const params = { uniform_item: selectedItem.value };
    if (selectedSize) params.size = selectedSize.value;
    else params.size_isnull = true;
    getWarehouseStockApi(selectedSource.value, params).then(response => {
      const rows = response.data?.results || response.data?.data || [];
      const total = rows.reduce((sum, r) => sum + (r.quantity || 0), 0);
      setAvailableQty(total);
    }).catch(() => setAvailableQty(null))
      .finally(() => setAvailableLoading(false));
  }, [selectedSource, selectedItem, selectedSize, sizeRequired]);

  useEffect(() => {
    setUniformItemsLoading(true);
    getUniformItemsApi({ page_size: 1000 }).then(response => {
      setUniformItems(
        (response.data?.results || [])
          .map(i => ({ label: i.name, value: i.id, sizes: i.sizes || [] }))
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
      );
    }).catch(() => {})
      .finally(() => setUniformItemsLoading(false));
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = {
      page: pageSettings.page,
      page_size: pageSettings.pageSize,
    };
    if (statusFilter) params.status = statusFilter;
    getStockTransfersApi(params).then(response => {
      setData(response.data?.results || []);
      setPageSettings(prev => ({ ...prev, total: response.data?.count || 0 }));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [pageSettings.page, pageSettings.pageSize, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChange = (pagination, filters, sorter) => {
    setPageSettings(handlePageChange(pagination, filters, sorter, pageSettings));
  };

  const handleComplete = (id) => {
    setActionLoading(id);
    completeStockTransferApi(id).then(() => {
      toast.success('Transfer completed', { position: "top-right", autoClose: 3000, transition: Bounce });
      setActionLoading(null);
      fetchData();
    }).catch(() => {
      toast.error('Failed to complete transfer', { position: "top-right", autoClose: 3000, transition: Bounce });
      setActionLoading(null);
    });
  };

  const handleCancel = (id) => {
    Modal.confirm({
      title: 'Cancel Transfer',
      content: 'Are you sure? Stock will be restored to the source warehouse.',
      okText: 'Yes, Cancel',
      okType: 'danger',
      onOk: () => {
        setActionLoading(id);
        cancelStockTransferApi(id).then(() => {
          toast.success('Transfer cancelled, stock restored', { position: "top-right", autoClose: 3000, transition: Bounce });
          setActionLoading(null);
          fetchData();
        }).catch(() => {
          toast.error('Failed to cancel transfer', { position: "top-right", autoClose: 3000, transition: Bounce });
          setActionLoading(null);
        });
      }
    });
  };

  const onCreateSubmit = (formData) => {
    if (formData.source_warehouse.value === formData.destination_warehouse.value) {
      setError('destination_warehouse', { type: 'manual', message: 'Must differ from source' });
      return;
    }
    setCreateLoading(true);
    createStockTransferApi({
      source_warehouse: formData.source_warehouse.value,
      destination_warehouse: formData.destination_warehouse.value,
      uniform_item: formData.uniform_item.value,
      size: formData.size?.value || null,
      quantity: formData.quantity,
      transfer_date: formData.transfer_date,
      notes: formData.notes || ''
    }).then(() => {
      toast.success('Transfer created', { position: "top-right", autoClose: 3000, transition: Bounce });
      setCreateLoading(false);
      setShowCreateModal(false);
      reset();
      fetchData();
    }).catch(e => {
      setCreateLoading(false);
      const data = e.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        data?.non_field_errors?.[0] ||
        (data && typeof data === 'object'
          ? Object.entries(data)
              .filter(([k]) => k !== 'status')
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
              .join(' | ')
          : null) ||
        'Failed to create transfer';
      toast.error(msg, { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  const columns = [
    { title: 'Source', dataIndex: 'source_warehouse_name' },
    { title: 'Destination', dataIndex: 'destination_warehouse_name' },
    { title: 'Item', dataIndex: 'uniform_item_name' },
    { title: 'Size', dataIndex: 'size_code' },
    { title: 'Qty', dataIndex: 'quantity' },
    { title: 'Date', dataIndex: 'transfer_date', render: (d) => formatDateHandler(d) },
    { title: 'Initiated By', dataIndex: 'initiated_by_name' },
    {
      title: 'Status', dataIndex: 'status',
      render: (status) => <span style={getStatusBadge(status)}>{status}</span>
    },
    {
      title: 'Action',
      render: (_, record) => {
        if (record.status !== 'PENDING') return null;
        return (
          <div className="d-flex gap-2">
            <Button label="Complete" className="btn btn-sm btn-success"
              loading={actionLoading === record.id} onClick={() => handleComplete(record.id)} />
            <Button label="Cancel" className="btn btn-sm btn-outline-danger"
              loading={actionLoading === record.id} onClick={() => handleCancel(record.id)} />
          </div>
        );
      }
    }
  ];

  const tabItems = [
    { key: '', label: 'All' },
    { key: 'PENDING', label: 'Pending' },
    { key: 'COMPLETED', label: 'Completed' },
    { key: 'CANCELLED', label: 'Cancelled' },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs maintitle="Stock Transfers" />
        <div className="d-flex justify-content-end mb-3">
<Button label="New Transfer" icon="fa fa-plus" className="btn btn-primary btn-sm"
            onClick={() => { reset(); setShowCreateModal(true); }} />
        </div>

        <Tabs activeKey={statusFilter} onChange={(key) => { setStatusFilter(key); setPageSettings(prev => ({ ...prev, page: 1 })); }}
          items={tabItems} />

        <Table
          columns={columns} dataSource={data} rowKey={(r) => r.id} loading={loading}
          pagination={{
            total: pageSettings.total, pageSize: pageSettings.pageSize, current: pageSettings.page,
            pageSizeOptions, itemRender, showSizeChanger: true
          }}
          onChange={handleChange} scroll={{ x: "max-content" }}
        />

        <EssFullWidthModal mask={false} title="Create Stock Transfer" open={showCreateModal}
          onCancel={() => setShowCreateModal(false)} className="full-width-modal">
          <div className="modal-content">
            <div className="modal-body">
              <form onSubmit={handleSubmit(onCreateSubmit)}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Source Warehouse <span className="text-danger">*</span></label>
                      <Controller name="source_warehouse" control={control}
                        render={({ field: { value, onChange } }) => (
                          <FilterSelect isClearable onChange={onChange} optionLabel="label" optionValue="value"
                            options={warehouseOptions} value={value} placeholder="Select Source" isLoading={warehousesLoading} />
                        )} />
                      {errors.source_warehouse && <small className="text-danger">{errors.source_warehouse.message}</small>}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Destination Warehouse <span className="text-danger">*</span></label>
                      <Controller name="destination_warehouse" control={control}
                        render={({ field: { value, onChange } }) => (
                          <FilterSelect isClearable onChange={onChange} optionLabel="label" optionValue="value"
                            options={warehouseOptions} value={value} placeholder="Select Destination" isLoading={warehousesLoading} />
                        )} />
                      {errors.destination_warehouse && <small className="text-danger">{errors.destination_warehouse.message}</small>}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Uniform Item <span className="text-danger">*</span></label>
                      <Controller name="uniform_item" control={control}
                        render={({ field: { value, onChange } }) => (
                          <FilterSelect isClearable
                            onChange={(v) => { onChange(v); setValue('size', null); }}
                            optionLabel="label" optionValue="value"
                            options={uniformItems} value={value} placeholder="Select Item" isLoading={uniformItemsLoading} />
                        )} />
                      {errors.uniform_item && <small className="text-danger">{errors.uniform_item.message}</small>}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">
                        Size {sizeRequired && <span className="text-danger">*</span>}
                        {!selectedItem && <small className="text-muted ms-2">(select an item first)</small>}
                        {selectedItem && !sizeRequired && <small className="text-muted ms-2">(no sizes assigned to this item)</small>}
                      </label>
                      <Controller name="size" control={control}
                        render={({ field: { value, onChange } }) => (
                          <FilterSelect isClearable onChange={onChange} optionLabel="label" optionValue="value"
                            options={sizeOptions} value={value}
                            placeholder={!selectedItem ? "Select an item first" : sizeRequired ? "Select Size" : "No sizes available"}
                            isDisabled={!selectedItem || !sizeRequired}
                            isLoading={false} />
                        )} />
                      {errors.size && <small className="text-danger">{errors.size.message}</small>}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-4">
                    <div className="input-block mb-3">
                      <label className="col-form-label d-flex justify-content-between align-items-center">
                        <span>Quantity <span className="text-danger">*</span></span>
                        {availableLoading && <small className="text-muted">Checking stock…</small>}
                        {!availableLoading && availableQty !== null && (
                          <small className={availableQty > 0 ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>
                            Available: {availableQty}
                          </small>
                        )}
                      </label>
                      <Controller name="quantity" control={control}
                        render={({ field }) => <input className="form-control" {...field} type="number" min="1"
                          max={availableQty ?? undefined} placeholder="Qty" />} />
                      {errors.quantity && <small className="text-danger">{errors.quantity.message}</small>}
                      {availableQty !== null && Number(watch('quantity')) > availableQty && (
                        <small className="text-danger d-block">Exceeds available stock</small>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Transfer Date <span className="text-danger">*</span></label>
                      <Controller name="transfer_date" control={control}
                        render={({ field }) => <input className="form-control" {...field} type="date" />} />
                      {errors.transfer_date && <small className="text-danger">{errors.transfer_date.message}</small>}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Notes</label>
                      <Controller name="notes" control={control}
                        render={({ field }) => <input className="form-control" {...field} placeholder="Notes" />} />
                    </div>
                  </div>
                </div>
                <div className="submit-section">
                  <Button loading={createLoading} disabled={createLoading} className="btn btn-primary submit-btn"
                    type="submit" label="Create Transfer" />
                </div>
              </form>
            </div>
          </div>
        </EssFullWidthModal>
      </div>
    </div>
  );
};

export default TransferList;
